import React from 'react';
import { View, Text, StyleSheet, Image, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import { fontStyle } from '../config/fonts';
import { Group, ZONE_TYPE_INFO } from '../types';
import PixelContainer from './PixelContainer';

interface ZoneSidebarProps {
  zone?: Group | null;
}

export default function ZoneSidebar({ zone }: ZoneSidebarProps) {
  const { colors } = useTheme();

  if (!zone) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background, borderLeftColor: colors.border }]}>
        <View style={styles.placeholder}>
          <Text style={[styles.placeholderText, fontStyle, { color: colors.textSecondary }]}>
            No zone selected
          </Text>
        </View>
      </View>
    );
  }

  const typeInfo = ZONE_TYPE_INFO[zone.type] || ZONE_TYPE_INFO.general;
  const formattedDate = zone.createdAt?.toDate?.()?.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) || 'Unknown';

  return (
    <View style={[styles.container, { backgroundColor: colors.background, borderLeftColor: colors.border }]}>
      {/* Zone Header */}
      <View style={[styles.header, { backgroundColor: colors.surface }]}>
        {zone.banner ? (
          <Image source={{ uri: zone.banner }} style={styles.banner} />
        ) : (
          <View style={[styles.bannerPlaceholder, { backgroundColor: typeInfo.color }]} />
        )}
        <View style={styles.zoneInfo}>
          {zone.image ? (
            <Image source={{ uri: zone.image }} style={styles.zoneImage} />
          ) : (
            <View style={[styles.zoneImagePlaceholder, { backgroundColor: colors.primary }]}>
              <Text style={styles.zoneImageText}>{zone.name.charAt(0).toUpperCase()}</Text>
            </View>
          )}
          <Text style={[styles.zoneName, fontStyle, { color: colors.text }]}>
            {zone.name}
          </Text>
          <View style={styles.typeTag}>
            <Text style={styles.typeEmoji}>{typeInfo.emoji}</Text>
            <Text style={[styles.typeLabel, fontStyle, { color: colors.textSecondary }]}>
              {typeInfo.label}
            </Text>
          </View>
        </View>
      </View>

      {/* Description */}
      {zone.description && (
        <View style={styles.descriptionSection}>
          <Text style={[styles.description, fontStyle, { color: colors.textSecondary }]}>
            {zone.description}
          </Text>
        </View>
      )}

      {/* Stats */}
      <View style={[styles.statsSection, { borderTopColor: colors.border }]}>
        <View style={styles.statItem}>
          <Ionicons name="globe-outline" size={16} color={colors.textSecondary} />
          <Text style={[styles.statLabel, fontStyle, { color: colors.textSecondary }]}>Public</Text>
        </View>
        <View style={styles.statItem}>
          <Ionicons name="calendar-outline" size={16} color={colors.textSecondary} />
          <Text style={[styles.statLabel, fontStyle, { color: colors.textSecondary }]}>
            Created {formattedDate}
          </Text>
        </View>
      </View>

      {/* Members Count */}
      <View style={[styles.membersSection, { borderTopColor: colors.border }]}>
        <View style={styles.membersStat}>
          <Text style={[styles.membersCount, fontStyle, { color: colors.text }]}>
            {zone.membersCount?.toLocaleString() || 0}
          </Text>
          <Text style={[styles.membersLabel, fontStyle, { color: colors.textSecondary }]}>
            Members
          </Text>
        </View>
      </View>

      {/* Join Button */}
      <View style={styles.actionSection}>
        <Pressable style={styles.joinButton}>
          <PixelContainer backgroundColor={colors.primary} cornerSize={4} style={styles.joinButtonInner}>
            <Text style={[styles.joinButtonText, fontStyle]}>Join</Text>
          </PixelContainer>
        </Pressable>
      </View>

      {/* Topics */}
      {zone.topics && zone.topics.length > 0 && (
        <View style={[styles.topicsSection, { borderTopColor: colors.border }]}>
          <Text style={[styles.topicsTitle, fontStyle, { color: colors.textSecondary }]}>
            TOPICS
          </Text>
          <View style={styles.topicsGrid}>
            {zone.topics.map((topic) => (
              <Pressable key={topic} style={[styles.topicTag, { backgroundColor: colors.surface }]}>
                <Text style={[styles.topicText, fontStyle, { color: colors.text }]}>
                  {topic}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 312,
    borderLeftWidth: Platform.OS === 'web' ? 1 : 0,
    ...(Platform.OS === 'web' ? {
      alignSelf: 'flex-start',
    } : {
      height: '100%',
    }),
  },
  placeholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderText: {
    fontSize: 14,
  },
  header: {
    borderRadius: 12,
    margin: 16,
    overflow: 'hidden',
  },
  banner: {
    width: '100%',
    height: 80,
  },
  bannerPlaceholder: {
    width: '100%',
    height: 80,
  },
  zoneInfo: {
    alignItems: 'center',
    paddingBottom: 16,
    marginTop: -24,
  },
  zoneImage: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 3,
    borderColor: '#fff',
  },
  zoneImagePlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 3,
    borderColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoneImageText: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '600',
  },
  zoneName: {
    fontSize: 16,
    fontWeight: '700',
    marginTop: 8,
  },
  typeTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  typeEmoji: {
    fontSize: 12,
  },
  typeLabel: {
    fontSize: 12,
  },
  descriptionSection: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
  },
  statsSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    gap: 8,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statLabel: {
    fontSize: 13,
  },
  membersSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    flexDirection: 'row',
  },
  membersStat: {
    alignItems: 'flex-start',
  },
  membersCount: {
    fontSize: 16,
    fontWeight: '700',
  },
  membersLabel: {
    fontSize: 12,
  },
  actionSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  joinButton: {
    width: '100%',
  },
  joinButtonInner: {
    paddingVertical: 10,
    alignItems: 'center',
  },
  joinButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  topicsSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
  topicsTitle: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  topicsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  topicTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  topicText: {
    fontSize: 12,
  },
});
